import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, UserPlus, Check, CircleAlert } from "lucide-react";
import { formatApiErrorDetail } from "../lib/api";

// Public self-service registration page (v1.37+).
// Flow:
//   1. Rep fills out the form (name, username, email, MA dropdown, password, 3 security answers)
//   2. POST /api/auth/register → backend creates a `status="pending"` user
//   3. Confirmation screen — they wait for an admin to review their answers and approve
// We deliberately do NOT log the user in or set any cookies on success — that's an admin's
// decision.

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC = `${API}/api/auth`;

// Live password-strength checklist. Backend validates these exact rules, so we just mirror
// them in the UI to give the rep instant feedback.
function checkPwRules(pw) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
}

export default function Register() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    master_agent_id: "",
    password: "",
    confirm: "",
    answers: ["", "", ""],
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [m, q] = await Promise.all([
          axios.get(`${PUBLIC}/master-agents`),
          axios.get(`${PUBLIC}/security-questions`),
        ]);
        if (!mounted) return;
        setAgents(m.data || []);
        setQuestions(q.data?.questions || []);
      } catch (_e) {
        // Non-fatal — the form simply won't have a dropdown until network recovers.
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const pwRules = useMemo(() => checkPwRules(form.password), [form.password]);
  const pwOk = pwRules.length && pwRules.upper && pwRules.digit && pwRules.symbol;
  const pwMatch = form.password && form.password === form.confirm;

  const canSubmit =
    form.name.trim().length >= 2 &&
    /^[a-z0-9_.-]{3,32}$/i.test(form.username) &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.master_agent_id &&
    pwOk &&
    pwMatch &&
    form.answers.every((a) => a.trim().length > 0) &&
    !busy;

  const setAnswer = (i, v) =>
    setForm((f) => ({ ...f, answers: f.answers.map((a, idx) => (idx === i ? v : a)) }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${PUBLIC}/register`, {
        name: form.name.trim(),
        username: form.username.trim().toLowerCase(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        master_agent_id: form.master_agent_id,
        security_answers: form.answers.map((a) => a.trim()),
      });
      setDone(true);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <main className="min-h-[90vh] grid place-items-center p-6 bg-slate-50" data-testid="page-register-done">
        <div className="w-full max-w-lg bg-white border-2 border-[#0B132B] p-10 text-center">
          <div className="mx-auto h-14 w-14 bg-[#00E5FF] grid place-items-center">
            <Check className="h-7 w-7 text-[#0B132B]" />
          </div>
          <h1 className="mt-5 text-3xl font-extrabold text-[#0B132B] tracking-tight">Registration submitted</h1>
          <p className="mt-3 text-slate-600">
            Thanks, <strong>{form.name.trim()}</strong>. An admin will review your information and
            you'll be able to sign in once they approve your account.
          </p>
          <button
            onClick={() => navigate("/admin/login")}
            className="btn-brand mt-8"
            data-testid="register-done-back"
          >
            Back to sign in
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[90vh] grid grid-cols-1 lg:grid-cols-2" data-testid="page-register">
      <div className="hidden lg:block bg-[#0B132B] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 mix-blend-screen" style={{ background: "radial-gradient(circle at 70% 80%, #00E5FF, transparent 50%)" }} />
        <div className="absolute inset-0 p-16 flex flex-col justify-between">
          <div>
            <div className="tw-overline !text-[#00E5FF]">New account</div>
            <h1 className="mt-3 text-5xl font-extrabold tracking-tighter">
              Join the<br/>TW Calc team.
            </h1>
            <p className="mt-4 text-slate-300 max-w-md">
              Pick your master agent, answer the security questions, and an admin will
              get you set up.
            </p>
          </div>
          <div className="mono text-xs text-slate-400 tracking-widest">// TWCALC-REGISTER v1</div>
        </div>
      </div>

      <div className="overflow-y-auto p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-xl mx-auto" data-testid="register-form">
          <UserPlus className="h-7 w-7 text-[#008B99]" />
          <h2 className="mt-3 text-3xl font-extrabold text-[#0B132B] tracking-tight">Create an account</h2>
          <p className="mt-1 text-sm text-slate-500">
            Already have one?{" "}
            <Link to="/admin/login" className="font-semibold text-[#008B99] hover:underline" data-testid="register-signin-link">Sign in</Link>
          </p>

          <div className="mt-8 space-y-5">
            <Field label="Full name" testid="register-name">
              <input
                className="input-sharp"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                autoComplete="name"
                data-testid="register-input-name"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Username" testid="register-username" help="3–32 chars: letters, numbers, dot, dash, underscore">
                <input
                  className="input-sharp"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/\s+/g, "") })}
                  required
                  autoComplete="username"
                  data-testid="register-input-username"
                />
              </Field>
              <Field label="Email" testid="register-email">
                <input
                  type="email"
                  className="input-sharp"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  autoComplete="email"
                  data-testid="register-input-email"
                />
              </Field>
            </div>

            <Field label="Master agent" testid="register-ma">
              <select
                className="input-sharp"
                value={form.master_agent_id}
                onChange={(e) => setForm({ ...form, master_agent_id: e.target.value })}
                required
                data-testid="register-input-ma"
              >
                <option value="">— Select your master agent —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {loading && <p className="text-xs text-slate-400 italic mt-1">Loading master agents…</p>}
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Password" testid="register-pw">
                <input
                  type="password"
                  className="input-sharp"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  autoComplete="new-password"
                  data-testid="register-input-password"
                />
              </Field>
              <Field label="Confirm password" testid="register-pwc">
                <input
                  type="password"
                  className="input-sharp"
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  required
                  autoComplete="new-password"
                  data-testid="register-input-confirm"
                />
              </Field>
            </div>

            {form.password.length > 0 && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="register-pw-rules">
                <RuleRow ok={pwRules.length} label="At least 8 characters" />
                <RuleRow ok={pwRules.upper} label="One uppercase letter" />
                <RuleRow ok={pwRules.digit} label="One number" />
                <RuleRow ok={pwRules.symbol} label="One symbol" />
                <RuleRow ok={pwMatch} label="Passwords match" />
              </div>
            )}

            <div className="pt-2">
              <div className="tw-overline">Security questions</div>
              <p className="text-xs text-slate-500 mt-1">An admin will review your answers before approving your account.</p>
              <div className="mt-3 space-y-4" data-testid="register-security-questions">
                {questions.map((q, i) => (
                  <Field key={i} label={`${i + 1}. ${q}`} testid={`register-q-${i + 1}`}>
                    <input
                      className="input-sharp"
                      value={form.answers[i]}
                      onChange={(e) => setAnswer(i, e.target.value)}
                      required
                      data-testid={`register-input-answer-${i + 1}`}
                    />
                  </Field>
                ))}
                {questions.length === 0 && !loading && (
                  <p className="text-xs text-slate-400 italic">Couldn't load security questions — please refresh.</p>
                )}
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="register-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="btn-brand w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create account
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

function Field({ label, children, testid, help }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="tw-overline">{label}</span>
      <div className="mt-2">{children}</div>
      {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
    </label>
  );
}

function RuleRow({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 ${ok ? "text-[#008B99]" : "text-slate-400"}`}>
      {ok ? <Check className="h-3 w-3" /> : <CircleAlert className="h-3 w-3" />}
      <span>{label}</span>
    </div>
  );
}
